/**
 * Custom error for coordinate validation failures.
 *
 * These are customer data problems (not internal errors) and should:
 * - Be shown directly to the user with a descriptive message
 * - NOT be sent to ErrorMonitoringService / Sentry
 *
 * The `name` field ("CoordinateValidationError") survives serialization across
 * the worker boundary via ErrorDetail, enabling discrimination on the main thread.
 */
export class CoordinateValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CoordinateValidationError";
  }

  /**
   * Type guard that works with both Error instances (in-worker) and
   * ErrorDetail plain objects (after worker serialization).
   */
  static isError(err: unknown): boolean {
    if (err instanceof CoordinateValidationError) {
      return true;
    }

    return (
      typeof err === "object" &&
      err !== null &&
      "name" in err &&
      (err as { name: unknown }).name === "CoordinateValidationError"
    );
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export type RawCoordinates = { latRaw: unknown; lonRaw: unknown };
export type ValidCoordinates = { latRaw: number; lonRaw: number };

/**
 * Validates raw coordinate values before normalization.
 * Checks that both values are finite numbers.
 *
 * @throws {CoordinateValidationError} If values are not finite numbers
 */
export function validateRawCoordinates(
  coords: RawCoordinates,
): asserts coords is ValidCoordinates {
  const latIsFiniteNumber = isFiniteNumber(coords.latRaw);
  const lonIsFiniteNumber = isFiniteNumber(coords.lonRaw);

  if (!latIsFiniteNumber && !lonIsFiniteNumber) {
    throw new CoordinateValidationError(
      "Latitude and longitude values are not finite numbers.",
    );
  }

  if (!latIsFiniteNumber) {
    throw new CoordinateValidationError(
      "Latitude value is not a finite number.",
    );
  }

  if (!lonIsFiniteNumber) {
    throw new CoordinateValidationError(
      "Longitude value is not a finite number.",
    );
  }
}

/**
 * Validates normalized coordinates are within valid geographic bounds.
 * Should be called after normalization.
 *
 * @throws {CoordinateValidationError} If coordinates are out of range
 */
export function validateNormalizedCoordinates(lat: number, lon: number): void {
  if (lat < -90 || lat > 90) {
    throw new CoordinateValidationError(
      `Invalid latitude value: ${lat}. Latitude must be between -90 and 90.`,
    );
  }

  if (lon < -180 || lon > 180) {
    throw new CoordinateValidationError(
      `Invalid longitude value: ${lon}. Longitude must be between -180 and 180.`,
    );
  }
}
